import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { RealtimeService } from './realtime.service';
import { SILENCE_LOADING } from './backend-http-context';

const WAKE_THRESHOLD_MS = 60_000;

interface ChangedResponse {
  since: string | null;
  now: string;
  changed: { task: boolean; sprint: boolean; resume: boolean };
}

/**
 * Al risveglio della tab (o al ritorno della rete) chiede al backend se
 * qualcosa è cambiato durante l'inattività e, solo se serve, inietta eventi
 * sintetici negli stream di RealtimeService così i componenti già
 * sottoscritti rifanno il fetch. Poi forza un reconnect ordinato (il check
 * ha già scaldato il backend). Handler a livello di pagina: nessun SW.
 */
@Injectable({ providedIn: 'root' })
export class WakeSyncService {
  hiddenAt: number | null = null;
  private inFlight = false;

  constructor(private http: HttpClient, private realtime: RealtimeService) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.markHidden();
      else this.markVisible();
    });
    window.addEventListener('online', () => this.wakeCheck());
  }

  markHidden(): void {
    this.hiddenAt = Date.now();
  }

  markVisible(): void {
    const at = this.hiddenAt;
    this.hiddenAt = null;
    if (at !== null && Date.now() - at >= WAKE_THRESHOLD_MS) this.wakeCheck();
  }

  wakeCheck(): void {
    const companyId = this.realtime.currentCompanyId;
    if (!companyId) return;
    if (this.inFlight) return;

    const since = this.realtime.lastSeen;
    let params = new HttpParams();
    if (since) params = params.set('since', since);

    this.inFlight = true;
    this.http
      .get<ChangedResponse>(`${environment.backendDomain}/api/company/${companyId}/changed`, {
        params,
        context: new HttpContext().set(SILENCE_LOADING, true),
      })
      .subscribe({
        next: (res) => {
          this.inFlight = false;
          if (res.changed.task) this.realtime.injectEvent('task.changed', { action: 'wake-sync' });
          if (res.changed.sprint) this.realtime.injectEvent('sprint.changed', { action: 'wake-sync' });
          if (res.changed.resume) this.realtime.injectEvent('resume.changed', { action: 'wake-sync' });
          this.realtime.setLastSeen(res.now);
          this.realtime.reconnect();
        },
        error: () => {
          this.inFlight = false;
          this.realtime.reconnect();
        },
      });
  }
}
